import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Loader2, Send } from "lucide-react";
import { createDoc, logActivity } from "../../lib/store";
import { usePublicData } from "../../context/PublicDataContext";

const inputCls =
  "w-full rounded-xl border border-[#0033FF]/12 bg-white px-4 py-3 text-[#0b1221] placeholder:text-[#9aa7bd] focus:outline-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent transition-shadow duration-200";

const empty = { name: "", email: "", message: "" };

export const Contact = () => {
  const { content } = usePublicData();
  const contactInfo = content.contact;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill all fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await createDoc("contacts", { ...form, status: "new" });
      await logActivity("contact", `New contact message from ${form.name}`);
      setForm(empty);
      toast.success("Message sent successfully! We'll get back to you soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    { icon: MessageCircle, label: "WhatsApp", value: contactInfo.whatsapp, href: contactInfo.whatsappLink },
    { icon: Mail, label: "Email", value: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { icon: MapPin, label: "Office", value: contactInfo.address, href: contactInfo.mapEmbed },
  ];

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#f4f7fb]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <p className="text-sm font-semibold text-[#0033FF] uppercase tracking-widest mb-3">
            Get in touch
          </p>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-5xl text-[#0b1221]">
            We&apos;d love to hear from you.
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.label}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-2xl bg-white border border-[#0033FF]/8 p-5 hover:border-[#0033FF]/25 hover:shadow-[0_14px_36px_rgba(0,51,255,0.1)] transition-[border-color,box-shadow] duration-300"
                  data-testid={`contact-info-${c.label.toLowerCase()}`}
                >
                  <span className="w-12 h-12 rounded-xl bg-[#0033FF]/8 text-[#0033FF] flex items-center justify-center group-hover:bg-[#0033FF] group-hover:text-white transition-colors duration-300 shrink-0">
                    <Icon className="w-6 h-6" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#5e6c84]">{c.label}</p>
                    <p className="font-medium text-[#0b1221]">{c.value}</p>
                  </div>
                </a>
              );
            })}

            <div className="rounded-2xl overflow-hidden border border-[#0033FF]/8 h-56">
              <iframe
                title="QuickDrop Go location"
                src={contactInfo.mapEmbed}
                className="w-full h-full border-0"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7 bg-white rounded-3xl p-8 md:p-10 border border-[#0033FF]/8 shadow-[0_20px_60px_rgba(0,51,255,0.08)]"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="block text-sm font-medium text-[#0b1221] mb-2">Name</span>
                <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-[#0b1221] mb-2">Email</span>
                <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="contact-email" />
              </label>
            </div>
            <label className="block mt-5">
              <span className="block text-sm font-medium text-[#0b1221] mb-2">Message</span>
              <textarea rows={6} className={inputCls + " resize-none"} value={form.message} onChange={set("message")} placeholder="How can we help?" data-testid="contact-message" />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] text-white font-semibold py-4 rounded-xl hover:bg-[#0026bf] transition-colors duration-200 disabled:opacity-70"
              data-testid="contact-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              {loading ? "Sending..." : "Send Message"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
